import { useState } from "react";
import { linkInputSchema, linksFromCsv, type LinkDraft as CoreDraft } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { keys, useAction } from "../../lib/queries";
import type { UpsertResult } from "../../lib/types";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/Feedback";
import { Field, Select, Textarea } from "../../ui/Form";
import { Segmented } from "../../ui/Misc";

/** `{ links: [...] }` (template kb:export, Web Dinamis /linkage.json, Console export) or a bare array. */
function parseJson(text: string): { drafts: CoreDraft[]; errors: string[] } {
  const raw = JSON.parse(text) as unknown;
  const list = Array.isArray(raw) ? raw : (raw as { links?: unknown }).links;
  if (!Array.isArray(list)) return { drafts: [], errors: ["File tidak berisi daftar `links`. File kb:export lama hanya berisi pengetahuan: jalankan ulang npm run kb:export."] };
  const drafts: CoreDraft[] = [];
  const errors: string[] = [];
  list.forEach((item, i) => {
    const parsed = linkInputSchema.safeParse({ key: (item as { url?: string }).url, ...(item as object) });
    if (parsed.success) drafts.push(parsed.data);
    else errors.push(`Butir ${i + 1}: ${parsed.error.issues[0]?.path.join(".")} ${parsed.error.issues[0]?.message}`);
  });
  return { drafts, errors };
}

export function LinkImportDialog({ projectId, open, onClose }: { projectId: string; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [format, setFormat] = useState<"json" | "csv">("json");
  const [text, setText] = useState("");
  const [mode, setMode] = useState<"append" | "replace">("append");
  let parsed: { drafts: CoreDraft[]; errors: string[] } = { drafts: [], errors: [] };
  if (text.trim()) {
    try {
      parsed = format === "json" ? parseJson(text) : linksFromCsv(text);
    } catch {
      parsed = { drafts: [], errors: ["JSON tidak valid."] };
    }
  }
  const run = useAction(
    (body: { mode: string; links: CoreDraft[] }) => api.post<UpsertResult>(`/projects/${projectId}/links/import`, body),
    [keys.links(projectId), keys.project(projectId)],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={680}
      title="Impor rujukan"
      description="Rujukan dicocokkan lewat key: impor ulang file yang sama memperbarui, tidak menggandakan."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            disabled={!parsed.drafts.length}
            loading={run.isPending}
            onClick={async () => {
              try {
                const result = await run.mutateAsync({ mode, links: parsed.drafts });
                toast(
                  `${result.created} baru, ${result.updated} diperbarui${result.deactivated ? `, ${result.deactivated} dinonaktifkan` : ""}${result.rejected.length ? `. ${result.rejected.length} ditolak: ${result.rejected[0]!.reason}` : "."}`,
                  result.rejected.length ? "critical" : "good",
                );
                setText("");
                onClose();
              } catch (failure) {
                toast(failure instanceof Error ? failure.message : "Impor gagal.", "critical");
              }
            }}
          >
            Impor {parsed.drafts.length ? `${parsed.drafts.length} rujukan` : ""}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        <Segmented label="Format" value={format} onChange={(value) => (setFormat(value), setText(""))} options={[{ value: "json", label: "JSON (kb:export)" }, { value: "csv", label: "CSV / Google Sheets" }]} />
        <Field label="File" hint={format === "csv" ? "Kolom wajib: judul, url. Opsional: ringkasan, harga, gambar, jenis (halaman/bagian/produk/artikel/dokumen), tag, kode." : "knowledge.json dari template Navibiz, atau ekspor Linkage proyek lain."}>
          {(id, describedBy) => (
            <input
              id={id}
              type="file"
              aria-describedby={describedBy}
              accept={format === "csv" ? ".csv,text/csv" : "application/json,.json"}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (file) setText(await file.text());
              }}
            />
          )}
        </Field>
        <Field label="…atau tempel isinya" error={parsed.errors[0] ? `${parsed.errors[0]}${parsed.errors.length > 1 ? ` (+${parsed.errors.length - 1} lagi)` : ""}` : null}>
          {(id, describedBy) => (
            <Textarea
              id={id}
              rows={8}
              value={text}
              aria-describedby={describedBy}
              onChange={(event) => setText(event.target.value)}
              placeholder={format === "csv" ? "judul,url,harga\nBuket mawar,https://toko.biz.id/buket,Rp 275.000" : '{ "links": [ { "key": "…", "kind": "page", "title": "…", "url": "…" } ] }'}
            />
          )}
        </Field>
        <Field label="Cara impor" hint={mode === "replace" ? "Rujukan hasil impor sebelumnya yang tidak ada di file ini dinonaktifkan. Rujukan manual, sitemap, dan feed tidak tersentuh." : "Rujukan baru ditambahkan, yang sudah ada diperbarui."}>
          {(id, describedBy) => (
            <Select id={id} value={mode} aria-describedby={describedBy} onChange={(event) => setMode(event.target.value as "append" | "replace")}>
              <option value="append">Tambahkan dan perbarui</option>
              <option value="replace">Samakan dengan file ini</option>
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  );
}
